import history from '../../../history'
import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { defineMessages, useIntl } from 'react-intl'
import { useMediaQuery } from 'react-responsive'
import isEmpty from 'lodash/isEmpty'
import { FadeOnMount } from '../../shared/Animations'
import { selectClientFavoriteListingsAlertCadenceDays, selectClientIsGuest } from '../../../store/selectors/client'
import { selectCustomerId } from '../../../store/selectors/customerProfile'
import { EmptyState } from '../EmptyState/EmptyState'
import { DelayedBotHeadSpinWithMessage, Text } from '../..'
import { setActiveNavigationTab } from '../../../store/slices/navigation'
import { NAVIGATION_TAB_NAMES } from '../../../types/navigation'
import styles from './FavoriteListings.module.scss'
import { useGetFavoriteListings } from '../../../hooks/gallery/useGetFavoriteListings'
import { ClientEventName, ClientEventSource, Listing } from '../../../api/gqlaxy/gql/generated/graphql'
import { ListingCardNew } from '../ListingCardNew/ListingCardNew'
import { Box, Button, Card, Flex, Grid, Heading, Icon } from '@homebotapp/hb-react-component-catalog'
import { hasCustomerToken } from '../../../auth'
import { HOME_SEARCH_FAVORITE_LISTINGS_PAGE } from '../../../constants/tracking/trackingEventLocations/homeSearchTrackingEventLocation'
import {
  ALERT_OFF_MIKASA,
  ALERT_OFF_STRING,
  alertCadenceMapper,
  ListingFavoriteAlertModal
} from '../ListingFavoriteAlertModal/ListingFavoriteAlertModal'
import { capitalizeFirstLetter } from '../Utils/format'
import { useCreateClientEvent, useEffectOncePerSessionConditionally } from '../../../hooks'

export const MSG = defineMessages({
  loading: {
    id: 'BotHeadSpinWithMessage.FavoriteListings',
    defaultMessage: 'Loading your favorite listings...'
  },
  back: {
    id: 'HomeSearch.favoriteListings.back',
    defaultMessage: 'Back'
  },
  heading: {
    id: 'HomeSearch.favoriteListings.heading',
    defaultMessage: 'Favorite listings'
  },
  emptyStateCopy: {
    id: 'HomeSearch.favoriteListings.emptyState.copy',
    defaultMessage: 'You have no favorite listings. Search for a city or zip, find a home and tap the heart icon.'
  },
  emptyStateCta: {
    id: 'HomeSearch.favoriteListings.emptyState.cta',
    defaultMessage: 'Start search'
  },
  subheading: {
    id: 'HomeSearch.favoriteListings.subheading',
    defaultMessage: 'We’ll keep you updated on important changes to your favorite properties'
  },
  directions: {
    id: 'HomeSearch.favoriteListings.directions',
    defaultMessage: 'You can customize alerts on specific properties'
  },
  defaultAlertPreferenceHeading: {
    id: 'HomeSearch.favoriteListings.defaultAlertPreferenceHeading',
    defaultMessage: 'Default alert settings'
  },
  manage: {
    id: 'HomeSearch.favoriteListings.manage',
    defaultMessage: 'Manage'
  },
  currentAlertSetting: {
    id: 'HomeSearch.favoriteListings.currentAlertSetting',
    defaultMessage: '{alertPreference} alerts {enabled}'
  },
  off: {
    id: 'HomeSearch.favoriteListings.off',
    defaultMessage: 'off'
  },
  on: {
    id: 'HomeSearch.favoriteListings.on',
    defaultMessage: 'on'
  },
  listing: {
    id: 'HomeSearch.favoriteListings.listing',
    defaultMessage: 'Listing'
  }
})

export const FavoriteListings = () => {
  const intl = useIntl()
  const isGuest = useSelector(selectClientIsGuest)
  const customerId = useSelector(selectCustomerId)
  const dispatch = useDispatch()
  const queryParams = new URLSearchParams(location.search)
  /** Snyk requires that we sanitize input from query params */
  const { data: listingsData, isLoading, refetch } = useGetFavoriteListings(undefined, { enabled: !isGuest })
  const listings = useMemo(() => listingsData?.favoriteListings.listings, [listingsData?.favoriteListings.listings])
  const clientIsGuest = useSelector(selectClientIsGuest)
  const isImpersonating = hasCustomerToken()
  const isMediumScreen = useMediaQuery({
    query: `(min-width: ${styles.mediumBreak})`
  })

  const defaultAlertPreferenceMikasa: number = useSelector(selectClientFavoriteListingsAlertCadenceDays) ?? 7
  const defaultAlertPreference = alertCadenceMapper(defaultAlertPreferenceMikasa) ?? 'weekly'

  // --------- Create listing viewed event START
  const { mutateAsync: createClientEvent } = useCreateClientEvent({ disableErrorPrompts: true })

  const createFavoriteListingsViewEvent = useCallback(() => {
    createClientEvent({
      clientEventInput: {
        eventName: ClientEventName.View,
        eventSource: ClientEventSource.FavoriteProperties
      }
    })
  }, [createClientEvent])

  useEffectOncePerSessionConditionally(createFavoriteListingsViewEvent, 'View_FavoriteProperties', Boolean(true))

  // --------- Create listing viewed event END

  useEffect(() => {
    // Set the active tab to home search when we are on the
    // favorite listings page.
    dispatch(setActiveNavigationTab(NAVIGATION_TAB_NAMES.SEARCH))
  }, [dispatch])

  if ((!clientIsGuest || isImpersonating) && isLoading) {
    return (
      <DelayedBotHeadSpinWithMessage loading={isLoading} className={styles.loadingContainer}>
        {intl.formatMessage(MSG.loading)}
      </DelayedBotHeadSpinWithMessage>
    )
  }

  return (
    customerId &&
    (!isLoading || (clientIsGuest && !isImpersonating)) && (
      <FadeOnMount>
        <Box pt={10} px={[5, 20]}>
          <Button mb={4} size='sm' leftIcon={<Icon name='angle-left' />} onClick={() => history.goBack()}>
            {intl.formatMessage(MSG.back)}
          </Button>
          <Flex pb={4} justifyContent={['center', 'flex-start']} alignItems='center'>
            <Text
              element='h1'
              size={isMediumScreen ? 'xl' : 's'}
              align='left'
              weight='semibold'
              variant='neutralCool'
              variantCount='900'
            >
              {intl.formatMessage(MSG.heading)}
            </Text>
            {listings && listings?.length > 0 && (
              <span>
                <Text
                  className={styles.headerBadge}
                  size='xxs'
                  weight='bold'
                  variant='neutralCool'
                  variantCount='100'
                  element='span'
                >
                  {listings?.length}
                </Text>
              </span>
            )}
          </Flex>
          <Flex justifyContent={'space-between'} flexWrap={'wrap'} mb={4}>
            <Box>
              <Flex justifyContent={'flex-start'} alignItems='center'>
                <Heading as='h2' size={'sm'} mb={0} textAlign={'left'}>
                  {intl.formatMessage(MSG.subheading)}
                </Heading>
              </Flex>
              <Flex justifyContent={'flex-start'} alignItems='center' pb={4}>
                <Text size='xs' weight='light'>
                  {intl.formatMessage(MSG.directions)}
                </Text>
              </Flex>
            </Box>

            <Card p={4} width={'100%'} maxW={['100%', '100%', 96]}>
              <Heading variant={'eyebrow'} size={'xs'} textAlign={'right'}>
                {intl.formatMessage(MSG.defaultAlertPreferenceHeading)}
              </Heading>
              <Flex justifyContent={'space-between'} alignItems={'center'}>
                <Flex flexWrap={'nowrap'} alignItems={'center'} gap={2}>
                  <Box
                    bgColor={defaultAlertPreferenceMikasa === ALERT_OFF_MIKASA ? 'neutral.300' : 'success.300'}
                    p={2}
                    borderRadius={'50%'}
                    width={8}
                    height={8}
                    display='flex'
                    justifyContent='center'
                    alignItems='center'
                  >
                    <Icon
                      name='bell-filled'
                      color={defaultAlertPreferenceMikasa === ALERT_OFF_MIKASA ? 'neutral.500' : 'success.500'}
                    />
                  </Box>
                  <Text weight='semibold'>
                    {intl.formatMessage(MSG.currentAlertSetting, {
                      alertPreference:
                        defaultAlertPreference === ALERT_OFF_STRING
                          ? intl.formatMessage(MSG.listing)
                          : capitalizeFirstLetter(defaultAlertPreference),
                      enabled:
                        defaultAlertPreferenceMikasa === ALERT_OFF_MIKASA
                          ? intl.formatMessage(MSG.off)
                          : intl.formatMessage(MSG.on)
                    })}
                  </Text>
                </Flex>
                <ListingFavoriteAlertModal mode='default' refetchFavoriteListings={refetch} />
              </Flex>
            </Card>
          </Flex>

          {listings && (
            <Grid templateColumns={['repeat(1, 1fr)', null, 'repeat(2, 1fr)', null, 'repeat(4, 1fr)']} gap={4}>
              {listings.map(listing => {
                return (
                  <ListingCardNew
                    listing={listing as Listing}
                    tracking={{
                      guid: '7qKDNiS5qErL7FcXiLuxPX',
                      ui_context: HOME_SEARCH_FAVORITE_LISTINGS_PAGE,
                      descriptor: 'Favorite Listing Clicked'
                    }}
                    showAlertPreferences
                    defaultAlertCadence={defaultAlertPreference}
                    refetchFavoriteListings={refetch}
                    key={listing?.id}
                  />
                )
              })}
            </Grid>
          )}
          {isEmpty(listings) && (
            <EmptyState copy={intl.formatMessage(MSG.emptyStateCopy)} ctaCopy={intl.formatMessage(MSG.emptyStateCta)} />
          )}
        </Box>
      </FadeOnMount>
    )
  )
}
